import React, { useContext, useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import {
  Autocomplete,
  Box,
  Chip,
  IconButton,
  InputLabel,
  TextField,
  Tooltip,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { BulkRow } from "@safetyculture/s12-apis-connect-web/s12/directory/v1/directory_pb";

import { BulkDownloadFoldersAPI } from "api/endpoints/sites";
import { callOptionsHeaders } from "api/helpers";
import { GrpcError, SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";
import { UrlParamTypes } from "types/org-display";

interface SitesDropdownProps {
  selectedSiteIds: string[];
  onSiteChange: (siteIds: string[]) => void;
}

const SitesDropdown = ({
  selectedSiteIds,
  onSiteChange,
}: SitesDropdownProps) => {
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const { showError } = useContext(SnackbarContext);
  const [sites, setSites] = useState<BulkRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchSites = async () => {
    if (!roleId) return;

    setIsLoading(true);
    try {
      const response = await BulkDownloadFoldersAPI(
        {
          OrgId: toUUID(roleId),
          IncludeDeleted: false,
        },
        callOptionsHeaders(regionCode, roleId)
      );
      // Filter to only show leaf sites (sites with hierarchy[0] values)
      const leafSites = response.row.filter(
        (site) =>
          site.hierarchy && site.hierarchy.length > 0 && site.hierarchy[0]
      );
      // Sort sites alphabetically by name
      const sortedSites = leafSites.sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setSites(sortedSites);
    } catch (error) {
      showError(error as GrpcError);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId, regionCode]);

  const handleChange = (_event: unknown, newValue: BulkRow[]) => {
    const siteIds = newValue.map((site) => site.folderId);
    onSiteChange(siteIds);
  };

  const selectedSites = sites.filter((site) =>
    selectedSiteIds.includes(site.folderId)
  );

  return (
    <>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, marginBottom: 1 }}
      >
        <InputLabel
          sx={{
            position: "static",
            transform: "none",
            fontWeight: 500,
          }}
        >
          Sites
        </InputLabel>
        <Tooltip title="Optional: Select specific sites for data generation, otherwise data generator will randomly pick a site for each generated item.">
          <IconButton size="small" sx={{ padding: 0 }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Autocomplete
        multiple
        options={sites}
        getOptionLabel={(option) => option.name}
        value={selectedSites}
        onChange={handleChange}
        disabled={isLoading}
        size="small"
        data-testid="sites-dropdown"
        disableCloseOnSelect={true}
        fullWidth
        limitTags={3}
        sx={{ minWidth: 300, maxWidth: 500 }}
        isOptionEqualToValue={(option, value) =>
          option.folderId === value.folderId
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={
              selectedSites.length === 0
                ? "All Sites - Type to search..."
                : "Type to search..."
            }
            sx={{ minWidth: 300 }}
          />
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.folderId}
              label={option.name}
              size="small"
            />
          ))
        }
        slotProps={{
          chip: {
            deleteIcon: <CloseIcon fontSize="small" />,
          },
        }}
      />
    </>
  );
};

export default SitesDropdown;
